/**
 * The product picker: one popover for two jobs.
 *
 *  - add:  the "+ Add job order" button in a cell opens it. Pick a product and
 *          an order is created in that cell the way a drop would be — first
 *          free shift, the product's run time, the next batch label. A
 *          "Full form" link keeps the long form for the odd case.
 *  - swap: a card's swap action opens it. Pick a product and the card changes
 *          in place, keeping its start time.
 *
 * The work centre narrows what is offered, never what is allowed: products
 * with a standard on this line are listed first, then what the plant made
 * lately, then cleaning and changeover. Everything else sits behind one
 * "Show N more" row so the ordinary case is a short list, and any search
 * looks through all of it. Picking a product with no standard here still
 * works; the board then says so when the order lands.
 */
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, List, ListItemButton, ListSubheader, Popover, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LaborStandard } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { planner } from "../../planner-ui";
import { ManufacturingOrder } from "../utils/calculations";
import { activityOrderType, standardsFor } from "./plan-edits";

export type PickerTarget =
	| { mode: "add"; workstation: string; day: Date }
	| { mode: "swap"; order: ManufacturingOrder };

export interface ProductPickerPopoverProps {
	anchorEl: HTMLElement | null;
	target: PickerTarget | null;
	products: Product[];
	standards: LaborStandard[];
	/** The loaded plan: what was made lately ranks above the long tail. */
	orders: ManufacturingOrder[];
	onClose: () => void;
	onAdd: (product: Product, workstation: string, day: Date) => void;
	onSwap: (order: ManufacturingOrder, product: Product) => void;
	/** "add" only: open the full job-order form for this cell instead. */
	onFullForm?: (workstation: string, day: Date) => void;
}

type Group = "line" | "recent" | "activity" | "other";

interface Option {
	product: Product;
	group: Group;
}

const GROUP_ORDER: Group[] = ["line", "recent", "activity", "other"];
const groupLabel = (key: Group, workstation: string): string =>
	key === "line" ? `Runs on ${workstation}` : key === "recent" ? "Made lately" : key === "activity" ? "Cleaning & changeover" : "Not set up for this line";

/** Case- and space-insensitive "every word of the query appears" match. */
export const matchesQuery = (name: string, query: string): boolean => {
	const haystack = name.toLowerCase();
	return query
		.toLowerCase()
		.split(/\s+/)
		.filter(Boolean)
		.every((word) => haystack.includes(word));
};

const LIST_MAX_HEIGHT = 360;

/**
 * The same colours the cards use: a product with a standard on this line is
 * accent (it will plan labor), cleaning and changeover take their activity
 * colour, and anything with no standard here is the grey the board gives an
 * unplanned block.
 */
const GROUP_COLOR: Record<Group, string> = {
	line: planner.accent,
	activity: planner.weld,
	recent: planner.unplanned,
	other: planner.unplanned,
};
const Dot: React.FC<{ group: Group }> = ({ group }) => {
	const filled = group === "line" || group === "activity";
	return (
		<Box
			component="span"
			aria-hidden
			sx={{
				width: 8,
				height: 8,
				borderRadius: "50%",
				flexShrink: 0,
				bgcolor: filled ? GROUP_COLOR[group] : "transparent",
				border: `1.5px solid ${GROUP_COLOR[group]}`,
			}}
		/>
	);
};

const ProductPickerPopover: React.FC<ProductPickerPopoverProps> = ({ anchorEl, target, products, standards, orders, onClose, onAdd, onSwap, onFullForm }) => {
	const workstation = target ? (target.mode === "add" ? target.workstation : target.order.workstation) : "";
	const excludeId = target && target.mode === "swap" ? target.order.productId : undefined;
	const open = Boolean(anchorEl && target);

	const [query, setQuery] = useState("");
	const [showOthers, setShowOthers] = useState(false);
	const [highlight, setHighlight] = useState(0);
	const listRef = useRef<HTMLUListElement>(null);

	// Each opening starts clean: empty search, long tail folded, first row lit.
	useEffect(() => {
		if (open) {
			setQuery("");
			setShowOthers(false);
			setHighlight(0);
		}
	}, [open, target]);

	const options = useMemo<Option[]>(() => {
		if (!target) return [];
		const recent = new Set(orders.map((o) => o.productId).filter((id): id is number => Boolean(id)));
		return products
			.filter((p) => p.isActive && p.id !== excludeId)
			.map<Option>((p) => {
				const group: Group = activityOrderType(p)
					? "activity"
					: standardsFor(p, workstation, standards).length
						? "line"
						: recent.has(p.id)
							? "recent"
							: "other";
				return { product: p, group };
			})
			.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) || a.product.name.localeCompare(b.product.name));
	}, [target, products, standards, orders, workstation, excludeId]);

	const searching = query.trim().length > 0;
	const matched = useMemo(() => (searching ? options.filter((o) => matchesQuery(o.product.name, query)) : options), [options, query, searching]);
	const othersCount = matched.filter((o) => o.group === "other").length;
	// A search looks through everything; otherwise the long tail stays folded.
	const visible = useMemo(() => (searching || showOthers ? matched : matched.filter((o) => o.group !== "other")), [matched, searching, showOthers]);

	useEffect(() => {
		setHighlight((h) => Math.min(h, Math.max(visible.length - 1, 0)));
	}, [visible.length]);

	const pick = useCallback(
		(option: Option) => {
			if (!target) return;
			if (target.mode === "add") onAdd(option.product, target.workstation, target.day);
			else onSwap(target.order, option.product);
			onClose();
		},
		[target, onAdd, onSwap, onClose]
	);

	const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setQuery(event.target.value);
		setHighlight(0);
	}, []);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				setHighlight((h) => Math.min(h + 1, visible.length - 1));
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				setHighlight((h) => Math.max(h - 1, 0));
			} else if (event.key === "Enter") {
				event.preventDefault();
				const option = visible[highlight];
				if (option) pick(option);
			} else if (event.key === "Escape") {
				onClose();
			}
		},
		[visible, highlight, pick, onClose]
	);

	// Keep the lit row in view while arrowing through a long list.
	useEffect(() => {
		const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${highlight}"]`);
		row?.scrollIntoView({ block: "nearest" });
	}, [highlight]);

	const handleShowOthers = useCallback(() => setShowOthers(true), []);
	const handleFullForm = useCallback(() => {
		if (target && target.mode === "add" && onFullForm) onFullForm(target.workstation, target.day);
		onClose();
	}, [target, onFullForm, onClose]);

	const heading = target?.mode === "swap" ? "Make something else here" : "What runs here?";
	const sub = target
		? target.mode === "swap"
			? `${target.order.sku || target.order.productName} · ${target.order.workstation} · keeps its start time`
			: `${target.workstation} · ${target.day.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · first free shift, run time from the standard`
		: "";

	// Rows carry their group header the first time the group appears.
	const rows: React.ReactNode[] = [];
	let lastGroup: Group | null = null;
	visible.forEach((option, index) => {
		if (option.group !== lastGroup) {
			lastGroup = option.group;
			rows.push(
				<ListSubheader
					key={`group-${option.group}`}
					disableSticky
					sx={{ ...planner.labelCaps, color: GROUP_COLOR[option.group], lineHeight: "28px", bgcolor: "transparent", px: 1 }}
				>
					{groupLabel(option.group, workstation)}
				</ListSubheader>
			);
		}
		rows.push(
			<ListItemButton
				key={option.product.id}
				data-index={index}
				selected={index === highlight}
				onClick={() => pick(option)}
				onMouseEnter={() => setHighlight(index)}
				dense
				title={
					option.group === "line"
						? `Has a standard on ${workstation}`
						: option.group === "activity"
							? "Cleaning or changeover activity"
							: `No standard on ${workstation} — run time will be the shift`
				}
				sx={{
					...planner.type.body,
					px: 1,
					py: 0.5,
					gap: 1,
					borderRadius: `${planner.radiusSm}px`,
					color: option.group === "line" || option.group === "activity" ? planner.ink : planner.inkSecondary,
					"&.Mui-selected": { bgcolor: planner.accentSoft },
					"&.Mui-selected:hover": { bgcolor: planner.accentSoft },
				}}
			>
				<Dot group={option.group} />
				<Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{option.product.name}
				</Box>
			</ListItemButton>
		);
	});

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			slotProps={{ paper: { sx: { p: 1.5, width: 360, border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, boxShadow: planner.shadowDialog } } }}
		>
			<Typography sx={{ ...planner.labelCaps, color: planner.inkMuted, mb: 0.75 }}>{heading}</Typography>
			<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary, mb: 1 }}>{sub}</Typography>
			<TextField
				value={query}
				onChange={handleQueryChange}
				onKeyDown={handleKeyDown}
				autoFocus
				fullWidth
				size="small"
				placeholder="Search products"
				inputProps={{ "aria-label": "Search products" }}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" sx={{ color: planner.inkMuted }} />
							</InputAdornment>
						),
					},
				}}
			/>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, px: 1, ...planner.type.meta, color: planner.inkMuted }}>
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					<Dot group="line" /> runs on {workstation}
				</Box>
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					<Dot group="other" /> no standard here
				</Box>
			</Box>
			<List ref={listRef} dense disablePadding sx={{ mt: 0.5, maxHeight: LIST_MAX_HEIGHT, overflowY: "auto" }}>
				{rows}
				{visible.length === 0 && (
					<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, px: 1, py: 1.5 }}>
						No product matches “{query.trim()}”.
					</Typography>
				)}
				{!searching && !showOthers && othersCount > 0 && (
					<ListItemButton dense onClick={handleShowOthers} sx={{ ...planner.type.meta, color: planner.inkSecondary, px: 1, py: 0.75, mt: 0.5, borderTop: `1px solid ${planner.hairline}` }}>
						Show {othersCount} more {othersCount === 1 ? "product" : "products"} not set up for this line
					</ListItemButton>
				)}
			</List>
			{target?.mode === "add" && onFullForm && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
					<Button size="small" onClick={handleFullForm} sx={{ ...planner.type.meta, textTransform: "none", color: planner.inkSecondary }}>
						Full form…
					</Button>
				</Box>
			)}
		</Popover>
	);
};

export default ProductPickerPopover;
